import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {ActionablePopupCloseButtonPosition, ActionablePopupPosition, type ActionablePopupProps, CLOSE_ICON, EXIT_ANIMATION_DURATION_MS, POSITION_CLASS} from './actionable-popup.types';
import {Text, TextSize, TextType} from '@Components/text';
import {useIsMounted} from '@Hooks/useIsMounted';
import styles from './actionable-popup.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import {Image} from 'react-bootstrap';
import {assertNever} from '@Utils/assert-never.util';

export function ActionablePopup({
  body,
  image,
  ctaButton,
  onClose,
  autoDismissAfterMs,
  position = ActionablePopupPosition.BOTTOM_RIGHT,
  closeButtonPosition = ActionablePopupCloseButtonPosition.TOP_RIGHT,
}: ActionablePopupProps): ReactElement {
  const hasMounted = useIsMounted();
  const [showDialog, setShowDialog] = useState(false);
  const hasClosedRef = useRef(false);

  useEffect(() => {
    setShowDialog(true);
  }, []);

  const finishClose = (callback: () => void): void => {
    if (hasClosedRef.current) {
      return;
    }
    hasClosedRef.current = true;
    setShowDialog(false);
    window.setTimeout(callback, EXIT_ANIMATION_DURATION_MS);
  };

  const handleCtaClick = (): void => {
    finishClose(ctaButton.onClick);
  };

  const handleDismiss = (): void => {
    finishClose(onClose);
  };

  useEffect(() => {
    if (autoDismissAfterMs === undefined) {
      return undefined;
    }
    const timerId = window.setTimeout(handleDismiss, autoDismissAfterMs);
    return (): void => {
      window.clearTimeout(timerId);
    };
  }, [autoDismissAfterMs]);

  const getWrapperClasses = (): string => {
    let wrapperStyles = `${styles.wrapper} ${POSITION_CLASS[position]}`;

    if (showDialog) {
      wrapperStyles += ` ${styles.in} `;
    } else {
      wrapperStyles += ` ${styles.out} `;
    }

    if (hasMounted.current) {
      wrapperStyles += ` ${styles.doAnimate} `;
    }

    if (closeButtonPosition === ActionablePopupCloseButtonPosition.TOP_RIGHT_ABOVE_DIALOG) {
      wrapperStyles += styles.hasFloatingCloseButton;
    }

    return wrapperStyles;
  };

  const getCloseIcon = (): ReactElement | null => {
    switch (closeButtonPosition) {
      case ActionablePopupCloseButtonPosition.NO_BUTTON:
        return null;
      case ActionablePopupCloseButtonPosition.TOP_RIGHT:
        return (
          <Icon type={IconType.DARK_GHOST} size={IconSize.SIZE_ICON_16} shape={IconShape.SQUARE} icon={CLOSE_ICON} className={styles.imageCloseButton} onClick={handleDismiss} />
        );
      case ActionablePopupCloseButtonPosition.TOP_RIGHT_ABOVE_DIALOG:
        return (
          <Icon
            type={IconType.DARK_GHOST}
            size={IconSize.SIZE_ICON_16}
            shape={IconShape.SQUARE}
            icon={CLOSE_ICON}
            className={styles.floatingCloseButton}
            onClick={handleDismiss}
          />
        );
      default:
        return assertNever(closeButtonPosition);
    }
  };

  const renderImage = (): ReactElement | null => {
    if (image === undefined) {
      return null;
    }
    return (
      <div className={styles.imageWrapper}>
        <Image className={styles.image} src={image.url} alt={image.altText} />
      </div>
    );
  };

  const renderBody = (): ReactElement | null => {
    if (body === undefined || body === '') {
      return null;
    }
    return <Text className={styles.body} type={TextType.BODY} size={TextSize.MD} val={body} />;
  };

  return (
    <div role="dialog" aria-label={body ?? ctaButton.text} className={getWrapperClasses()}>
      {getCloseIcon()}
      <div className={styles.clippedContent}>
        {renderImage()}
        <div className={styles.content}>
          {renderBody()}
          <Button type={ctaButton.type ?? ButtonType.PRIMARY} size={ButtonSize.MEDIUM} isFullWidth text={ctaButton.text} onClick={handleCtaClick} />
        </div>
      </div>
    </div>
  );
}
